import React from "react";
import styled from "styled-components";

import { ACCENT, ACCENT_TINT } from "./accent";
import { Part, PartHead, PartTitle, PartTotal, DIMMED, shareOf, lossesOf } from "./BreakdownPart";
import { hasLoss } from "./rows";

const MIN_SEGMENT_SHARE = 0.01;

/**
 * The drop dashboard's "Lost by likely cause": one stacked bar of the causes' impact in proportion to
 * each other, then one line per cause, read from the list as it would be with that cause selected, with
 * its share of the total impact and its loss a month on each of `measures`.
 * A row with two causes counts in both, so the shares of the total can add up to more than 100%.
 * A cause under 1% of the stacked total has no segment.
 * Selecting a cause filters the list, highlights its line and dims the others; selecting it again
 * clears the filter.
 */
export default function CauseBreakdown({ causes, total, measures, selectedCause, onSelect }) {
    const lossShown = measures.length > 0;
    const stacked = causes.reduce((sum, { impact }) => sum + impact, 0);
    const segments = causes.filter(({ impact }) => impact > 0 && impact >= stacked * MIN_SEGMENT_SHARE);
    const segmented = segments.reduce((sum, { impact }) => sum + impact, 0);
    return (
        <Part>
            <PartHead>
                <PartTitle>{lossShown ? "Est. loss by cause" : "Issues by cause"}</PartTitle>
                {lossShown && <PartTotal>{lossesOf(total.lostPerMonth, measures)} /month</PartTotal>}
            </PartHead>
            {lossShown && stacked > 0 && (
                <Stack aria-hidden>
                    {segments.map(({ cause, impact }) => (
                        <span key={cause.key} style={{ width: `${(impact / segmented) * 100}%`, background: cause.color }} />
                    ))}
                </Stack>
            )}
            <Causes>
                {causes.map(({ cause, rows, impact, lostPerMonth }) => (
                    <li key={cause.key}>
                        <CauseButton type="button" $selected={cause.key === selectedCause} $dimmed={selectedCause !== null && cause.key !== selectedCause}
                            aria-pressed={cause.key === selectedCause} onClick={() => onSelect(cause.key)}>
                            <Square $color={cause.color} />
                            <Name>{cause.label}<Rows>{rows.toLocaleString()} {rows === 1 ? "row" : "rows"}</Rows></Name>
                            {lossShown && <Share>{shareOf(impact, total.impact)}</Share>}
                            {lossShown && <Lost>{hasLoss({ lostPerMonth }) ? lossesOf(lostPerMonth, measures) : "–"}</Lost>}
                        </CauseButton>
                    </li>
                ))}
            </Causes>
        </Part>
    );
}

const Stack = styled.div`
    display: flex;
    height: 10px;
    overflow: hidden;
    margin-top: 10px;
    border-radius: 999px;
    span { height: 100%; border-right: 2px solid #fff; }
    span:last-child { border-right: none; }
`;

/** One grid for every cause line, so the shares and the losses line up in columns whatever the losses' width. */
const Causes = styled.ul`
    display: grid;
    grid-template-columns: 8px minmax(0, 1fr) auto auto;
    column-gap: 8px;
    margin: 8px 0 0;
    padding: 0;
    list-style: none;
    > li { display: grid; grid-column: 1 / -1; grid-template-columns: subgrid; }
    @media (max-width: 600px) { display: block; }
`;

const CauseButton = styled.button`
    display: grid;
    grid-column: 1 / -1;
    grid-template-columns: subgrid;
    align-items: center;
    margin: 0 -8px;
    padding: 4px 8px;
    border: none;
    border-radius: 6px;
    background: ${({ $selected }) => ($selected ? ACCENT_TINT : "none")};
    box-shadow: ${({ $selected }) => ($selected ? `inset 3px 0 0 ${ACCENT}` : "none")};
    opacity: ${({ $dimmed }) => ($dimmed ? DIMMED : 1)};
    font: inherit;
    font-size: 13px;
    text-align: left;
    cursor: pointer;
    > span:nth-child(2) { font-weight: ${({ $selected }) => ($selected ? 600 : 400)}; }
    &:hover { background: ${({ $selected }) => ($selected ? ACCENT_TINT : "#f5f6f8")}; opacity: 1; }
    @media (max-width: 600px) {
        display: flex;
        gap: 8px;
        width: calc(100% + 16px);
        flex-wrap: wrap;
        row-gap: 2px;
        line-height: 1.25;
        > span:nth-child(2) { flex-basis: calc(100% - 16px); }
        > span:nth-child(3) { margin-left: auto; }
    }
`;

const Square = styled.span`
    width: 8px;
    height: 8px;
    border-radius: 2px;
    flex-shrink: 0;
    background: ${({ $color }) => $color};
`;

const Name = styled.span`
    flex: 1;
    min-width: 0;
    color: #0f1419;
`;

const Rows = styled.span`
    margin-left: 6px;
    font-size: 12px;
    font-weight: 400;
    color: #8b94a3;
`;

const Share = styled.span`
    text-align: right;
    font-variant-numeric: tabular-nums;
    color: #5a6473;
`;

const Lost = styled.span`
    text-align: right;
    white-space: nowrap;
    font-weight: 500;
    font-variant-numeric: tabular-nums;
    color: #0f1419;
`;
